import React, { useMemo, useState } from 'react';
import { ChevronLeftIcon, CloudOffIcon, StickyNoteIcon, WifiIcon } from 'lucide-react';
import { NotesSidebar } from '../../components/notes/NotesSidebar';
import { NoteList } from '../../components/notes/NoteList';
import type { SortKey } from '../../components/notes/NoteList';
import { NoteEditorPane } from '../../components/notes/NoteEditorPane';
import { DiaryRail } from '../../components/notes/DiaryRail';
import { TemplatePicker } from '../../components/notes/TemplatePicker';
import { EmptyState } from '../../components/ui/EmptyState';
import { Button } from '../../components/ui/Button';
import { useNotes } from '../../contexts/NotesContext';
import { useDataMode } from '../../contexts/DataModeContext';
import { FirstRun } from '../../components/platform/FirstRun';
import { searchIndex } from '../../utils/notes';
import type { NotesSelection } from '../../types/notes';
const HEADINGS: Record<string, string> = {
  all: 'All notes',
  recent: 'Recent',
  pinned: 'Pinned',
  shared: 'Shared with me',
  diary: 'My Diary',
  archive: 'Archive',
  trash: 'Trash'
};
export function Notes() {
  const {
    notes,
    ownNotes,
    sharedWithMe,
    folders,
    offline,
    setOffline,
    createNote
  } = useNotes();
  const [selection, setSelection] = useState<NotesSelection>({
    view: 'all'
  });
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<SortKey>('edited');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>('n-1');
  const [templateOpen, setTemplateOpen] = useState(false);
  const [mobilePane, setMobilePane] = useState<'list' | 'editor'>('list');
  const {
    isNew
  } = useDataMode();
  if (isNew) {
    return <FirstRun surface="Notes" what="Your own notebook and diary, private by default." why="Lecture notes, checklists, tables, drawings and voice recordings in one place, searchable by anything in them — including what you said out loud. Notes can be linked to a course or an application without ever changing who owns them." note="Every note starts private. Your university, lecturers, administrators and classmates cannot see your notes or diary. Sharing is something you do deliberately, one note at a time, and you can revoke it." steps={[{
      label: 'Capture something now',
      detail: 'The Capture button in the top bar works from any screen. Quick notes land unfiled and can be organised later.'
    }, {
      label: 'Start from a template',
      detail: 'Lecture, reading, lab, meeting, revision, project and diary templates are all editable.'
    }, {
      label: 'Set a PIN if you want one',
      detail: 'It guards against someone holding your unlocked device. Authorisation is enforced separately by the service.'
    }]} data-id="element-11390" data-mp="2xufwxcu55c3u" />;
  }
  const visible = useMemo(() => {
    const searching = query.trim().length > 0;
    let pool = selection.view === 'shared' ? sharedWithMe : ownNotes;
    if (selection.view === 'trash') {
      pool = pool.filter(note => note.lifecycle === 'trashed');
    } else if (selection.view === 'archive') {
      pool = pool.filter(note => note.lifecycle === 'archived');
    } else {
      pool = pool.filter(note => note.lifecycle === 'active' || includeArchived && note.lifecycle === 'archived');
    }
    if (selection.view === 'pinned') pool = pool.filter(note => note.pinned);
    if (selection.view === 'diary') pool = pool.filter(note => Boolean(note.diaryDate));
    if (selection.view === 'folder') pool = pool.filter(note => note.folderId === selection.folderId);
    if (selection.view === 'tag') pool = pool.filter(note => note.tags.includes(selection.tag ?? ''));
    if (searching) {
      const q = query.trim().toLowerCase();
      pool = pool.filter(note => searchIndex(note, folders).includes(q));
    }
    const sorted = [...pool].sort((a, b) => {
      if (sort === 'title') return a.title.localeCompare(b.title);
      if (sort === 'created') return a.createdAt < b.createdAt ? 1 : -1;
      return a.updatedAt < b.updatedAt ? 1 : -1;
    });

    // Pinned notes lead every list except the lifecycle views.
    if (selection.view === 'all' || selection.view === 'folder' || selection.view === 'tag') {
      return [...sorted.filter(note => note.pinned), ...sorted.filter(note => !note.pinned)];
    }
    if (selection.view === 'diary') {
      return sorted.sort((a, b) => (a.diaryDate ?? '') < (b.diaryDate ?? '') ? 1 : -1);
    }
    return sorted;
  }, [notes, ownNotes, sharedWithMe, selection, query, sort, includeArchived, folders]);
  const heading = selection.view === 'folder' ? folders.find(folder => folder.id === selection.folderId)?.name ?? 'Folder' : selection.view === 'tag' ? `#${selection.tag}` : HEADINGS[selection.view];
  const selected = notes.find(note => note.id === selectedId) ?? null;
  function openNote(id: string) {
    setSelectedId(id);
    setMobilePane('editor');
  }
  function newNote(template?: Parameters<typeof createNote>[0]) {
    const id = createNote({
      ...template,
      folderId: selection.view === 'folder' ? selection.folderId : null,
      diary: selection.view === 'diary',
      template: selection.view === 'diary' ? 'diary' : template?.template
    });
    openNote(id);
  }
  return <div className="space-y-4" data-id="element-11391" data-mp="237nv0lr92lfw">
      <div className="flex flex-wrap items-center gap-3" data-id="element-11392" data-mp="236miecul5qw2">
        <div className="flex min-w-0 flex-1 items-center gap-2.5" data-id="element-11393" data-mp="2i4yfgklvf1nr">
          <StickyNoteIcon className="h-5 w-5 shrink-0 text-brand-700" data-id="element-11394" data-mp="2i2c56d7li81u" />
          <div className="min-w-0" data-id="element-11395" data-mp="2d5v9mxy2greq">
            <h1 className="text-xl font-semibold leading-7 text-ink" data-id="element-11396" data-mp="nqdyoj0o7q9n">Campuzon Notes</h1>
            <p className="text-[13px] leading-5 text-ink-muted" data-id="element-11397" data-mp="nsi2z22herag">
              Your own notebook. Private by default, and yours to take with you.
            </p>
          </div>
        </div>
        <Button variant="secondary" size="sm" icon={offline ? <CloudOffIcon className="h-3.5 w-3.5" data-id="element-11399" data-mp="1chjvxguy7rg1" /> : <WifiIcon className="h-3.5 w-3.5" data-id="element-11400" data-mp="1hg4nkrw4x2sv" />} onClick={() => setOffline(!offline)} data-id="element-11398" data-mp="18hj7r4i1s1vs">
          {offline ? 'Reconnect' : 'Simulate offline'}
        </Button>
      </div>

      <div className="overflow-hidden rounded-card border border-line bg-surface" data-id="element-11401" data-mp="1mcv39e411wdr">
        <div className="grid lg:grid-cols-[236px_320px_minmax(0,1fr)]" data-id="element-11402" data-mp="1rc7zgpgsgft0">
          {/* Sidebar — hidden on mobile once a note is open. */}
          <div className={`border-line lg:block lg:border-r ${mobilePane === 'editor' ? 'hidden' : 'block border-b lg:border-b-0'}`} data-id="element-11403" data-mp="206476z97hpow">
            <NotesSidebar selection={selection} onSelect={next => {
            setSelection(next);
            setMobilePane('list');
          }} onNewNote={() => setTemplateOpen(true)} data-id="element-11404" data-mp="2a4sfv7fawsfx" />
          </div>

          {/* Note list. */}
          <div className={`min-h-[420px] border-line lg:block lg:border-r ${mobilePane === 'editor' ? 'hidden' : 'block border-b lg:border-b-0'}`} data-id="element-11405" data-mp="3umuld5wun95d">
            <NoteList notes={visible} selection={selection} heading={heading} query={query} onQuery={setQuery} sort={sort} onSort={setSort} includeArchived={includeArchived} onIncludeArchived={setIncludeArchived} selectedId={selectedId} onSelectNote={openNote} onNewNote={() => setTemplateOpen(true)} data-id="element-11406" data-mp="3kowusbrip09l" />
          </div>

          {/* Editor. */}
          <div className={`min-h-[560px] ${mobilePane === 'list' ? 'hidden lg:block' : 'block'}`} data-id="element-11407" data-mp="14xsg5y62fy5c">
            <button type="button" onClick={() => setMobilePane('list')} className="flex w-full items-center gap-1.5 border-b border-line px-4 py-2.5 text-[13px] font-medium text-ink-muted lg:hidden" data-id="element-11408" data-mp="106k5yzwww8r4">
              <ChevronLeftIcon className="h-4 w-4" data-id="element-11409" data-mp="dbsiqp00n8hw" />
              Back to notes
            </button>

            {selection.view === 'diary' && <DiaryRail entries={visible} selectedId={selectedId} onSelect={openNote} onNewEntry={() => newNote({
            template: 'diary',
            diary: true
          })} data-id="element-11410" data-mp="4ip62ix72zob" />}

            {selected ? <NoteEditorPane note={selected} onDeleted={() => setSelectedId(null)} data-id="element-11411" data-mp="i9aq4aydxfns" /> : <EmptyState icon={<StickyNoteIcon className="h-4 w-4" data-id="element-11413" data-mp="n6u3l8n9doeo" />} title="Nothing open" description="Choose a note from the list, or start a new one. Everything you write here is private until you decide otherwise." action={<Button variant="primary" onClick={() => setTemplateOpen(true)} data-id="element-11414" data-mp="30eby50tb3yj1">
                    New note
                  </Button>} data-id="element-11412" data-mp="n7m1wr88q08n" />}
          </div>
        </div>
      </div>

      <TemplatePicker open={templateOpen} onClose={() => setTemplateOpen(false)} onPick={template => newNote({
      template
    })} data-id="element-11415" data-mp="39egvei4sdgjh" />
    </div>;
}